<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useRegisterSW } from 'virtual:pwa-register/vue';
import axios from './../axios/axios'; // axios instance'ımızı import ediyoruz



// check for updates every hour
const period = 60 * 60 * 1000;

const swActivated = ref(false);
const isOnline = ref(navigator.onLine); // İnternet bağlantı durumu
const isApiAvailable = ref(true); // API durumu



/**
 * API durumunu kontrol eder
 */
async function checkApiStatus() {
    if (!isOnline.value) return;

    try {
        // fetch yerine axios kullanıyoruz
        const response = await axios.get('v1/health');
        isApiAvailable.value = response.status === 200;
    } catch (error) {
        console.log(error)
        isApiAvailable.value = false;
    }
}

/**
 * İnternet bağlantı durumunu yönetir
 */
function handleConnectionChange() {
    isOnline.value = navigator.onLine;
    if (isOnline.value) {
        checkApiStatus();
    }
}

// Bağlantı olaylarını dinle
onMounted(() => {
    window.addEventListener('online', handleConnectionChange);
    window.addEventListener('offline', handleConnectionChange);
    // İlk API kontrolü
    checkApiStatus();
    // Periyodik API kontrolü (5 dakikada bir)
    const apiCheckInterval = setInterval(checkApiStatus, 300000);

    onUnmounted(() => {
        window.removeEventListener('online', handleConnectionChange);
        window.removeEventListener('offline', handleConnectionChange);
        clearInterval(apiCheckInterval);
    });
});

/**
 * Service Worker güncelleme kontrolü
 * @param {string} swUrl
 * @param {ServiceWorkerRegistration} r
 */
function registerPeriodicSync(swUrl, r) {
    if (period <= 0) return;

    setInterval(async () => {
        if (!isOnline.value) return;

        const resp = await fetch(swUrl, {
            cache: 'no-store',
            headers: {
                cache: 'no-store',
                'cache-control': 'no-cache'
            }
        });

        if (resp?.status === 200) await r.update();
    }, period);
}

const { offlineReady, needRefresh, updateServiceWorker } = useRegisterSW({
    immediate: true,
    onRegisteredSW(swUrl, r) {
        if (period <= 0) return;
        if (r?.active?.state === 'activated') {
            swActivated.value = true;
            registerPeriodicSync(swUrl, r);
        } else if (r?.installing) {
            r.installing.addEventListener('statechange', (e) => {
                /** @type {ServiceWorker} */
                const sw = e.target;
                swActivated.value = sw.state === 'activated';
                if (swActivated.value) registerPeriodicSync(swUrl, r);
            });
        }
    }
});

const title = computed(() => {
    if (!isOnline.value) return 'İnternet Bağlantısı Yok';
    if (!isApiAvailable.value) return 'Sunucu Bağlantısı Yok';
    if (offlineReady.value) return 'Uygulama çevrimdışı çalışmaya hazır';
    if (needRefresh.value) return 'Uygulama güncellendi.';
    return '';
});

const desc = computed(() => {
    if (!isOnline.value) return 'Lütfen internet bağlantınızı kontrol edin.';
    if (!isApiAvailable.value) return 'Sunucuya erişilemiyor. Lütfen daha sonra tekrar deneyin.';
    if (offlineReady.value) return 'Uygulama çevrimdışı çalışmaya hazır';
    if (needRefresh.value) return 'İşleminizi tamamladıktan sonra son sürümü kullanmak için lütfen güncellemeyi onaylayın. Güncelleme onayını işlem sırasında yapmanız durumunda işleminiz iptal edilecektir.';
    return '';
});

// Bildirim gösterilme durumu
const showNotification = computed(() => {
    return !isOnline.value || !isApiAvailable.value || offlineReady.value || needRefresh.value;
});

function close() {
    // Sadece offline ready ve refresh durumlarını kapat
    if (offlineReady.value) offlineReady.value = false;
    if (needRefresh.value) needRefresh.value = false;
    // Bağlantı sorunları otomatik kapanacak
}
</script>

<template>
    <div v-if="showNotification" class="pwa-toast" aria-labelledby="toast-message" role="alert">
        <Message :closable="false" icon="null" class="surface-300" style="margin: 0">
            <div class="my-3 text-xl text-gray-900">{{ title }}</div>
            <div class="my-3 text-base text-gray-900">{{ desc }}</div>
            <Button v-if="needRefresh" severity="contrast" class="p-button-sm mr-2" label="Güncellemeyi Onayla"
                @click="updateServiceWorker()" />
            <Button v-if="offlineReady || needRefresh" severity="contrast" class="p-button-text" label="Sonra Hatırlat"
                @click="close()" />
        </Message>
    </div>
</template>

<style scoped>
.pwa-toast {
    position: fixed;
    right: 0;
    bottom: 0;
    margin: 16px;
    padding: 0;
    z-index: 1;
    text-align: left;
    display: grid;
    width: 100%;
}

.pwa-toast button.reload {
    display: block;
}

@media (min-width: 700px) {
    .pwa-toast {
        width: 35%;
    }
}
</style>